import React, { useEffect, useState } from "react";
import {
  ArrowRight,
  Check,
  FileText,
  PenLine,
  Layers3,
  Loader2,
  Sparkles,
  WandSparkles,
} from "lucide-react";
import { useNavigate } from "react-router-dom";

import BrandStudioShellV2 from "@/components/brandStudio/BrandStudioShellV2";
import { readBrandWorkspaceSourcesV1 } from "../../lib/brandWorkspaceReadV1";
import { assembleMyBrand } from "../../lib/myBrandPresentationV1.mjs";

const CREATE_PREVIEW = [
  { id: "identity", title: "Фирменный стиль", note: "Логотип, цвет, типографика и графический язык" },
  { id: "social", title: "Социальные сети", note: "Публикации, истории, обложки и серии" },
  { id: "business", title: "Материалы для бизнеса", note: "Презентации, сертификаты, прайсы и документы" },
  { id: "sites", title: "Сайт", note: "От визитки до лендинга — в твоём фирменном мире" },
];

function PossibilityRail({ nav }) {
  return (
    <aside
      className="flex min-w-0 flex-col rounded-[18px] border border-[#DDD0BE] bg-[#FBF7F0] p-4 lg:h-full"
      aria-labelledby="brand-studio-create-preview"
      data-testid="brand-studio-home-possibilities"
    >
      <div className="flex items-end justify-between gap-3">
        <div>
          <div className="text-[9.5px] uppercase tracking-[0.22em] text-[#8A6D53]">ВОЗМОЖНОСТИ</div>
          <h2 id="brand-studio-create-preview" className="mt-1 font-serif text-[25px] leading-none text-[#2B211A]">
            Что можно создать
          </h2>
        </div>
        <button
          type="button"
          onClick={() => nav("/brand-studio/possibilities")}
          className="hidden min-h-10 items-center gap-1 text-[12px] text-[#6D5842] xl:inline-flex"
        >
          Все <ArrowRight size={13} />
        </button>
      </div>

      <div className="mt-4 grid flex-1 grid-cols-2 auto-rows-fr gap-3">
        {CREATE_PREVIEW.map((item, index) => (
          <button
            key={item.title}
            type="button"
            onClick={() => nav(`/brand-studio/possibilities?category=${item.id}`)}
            data-testid={`brand-studio-home-category-${item.id}`}
            className="min-h-[138px] rounded-[14px] border border-[#E3D7C6] bg-[#FFFDF9] p-4 text-left transition hover:-translate-y-0.5 hover:shadow-sm"
          >
            <span className="grid h-8 w-8 place-items-center rounded-full bg-[#F1E6D6] text-[#765D46]">
              {index === 0 ? <Sparkles size={14} /> : <Layers3 size={14} />}
            </span>
            <span className="mt-4 block font-serif text-[19px] leading-[1.02] text-[#2B211A]">{item.title}</span>
            <span className="mt-2 block text-[11.5px] leading-4.5 text-[#77634F]">{item.note}</span>
          </button>
        ))}
      </div>

      <button
        type="button"
        onClick={() => nav("/brand-studio/possibilities")}
        className="mt-3 inline-flex min-h-10 items-center gap-1 text-[12px] text-[#6D5842] xl:hidden"
      >
        Все возможности <ArrowRight size={13} />
      </button>
    </aside>
  );
}

export default function MOBrandStudioHomeV2() {
  const nav = useNavigate();
  const [status, setStatus] = useState("loading");
  const [story, setStory] = useState(null);
  const [workspace, setWorkspace] = useState(null);

  const load = async () => {
    setStatus("loading");
    try {
      const data = await readBrandWorkspaceSourcesV1();
      setStory(data.story || null);
      setWorkspace(assembleMyBrand(data));
      setStatus("ready");
    } catch {
      setStatus("error");
    }
  };

  useEffect(() => {
    load();
  }, []);

  const hasStory = Boolean(story);
  const completed = workspace?.state === "ready";

  return (
    <BrandStudioShellV2>
      <div className="mx-auto max-w-[1180px] pb-4" data-testid="brand-studio-v2-home">
        <div className="grid min-w-0 gap-5 lg:grid-cols-[minmax(0,1.62fr)_minmax(340px,0.78fr)] lg:items-stretch">
          <div className="min-w-0">
            <section
              className="overflow-hidden rounded-[18px] border border-[#E2D6C5] bg-[#F8F2E9] md:hidden"
              data-testid="brand-studio-v2-hero-mobile"
            >
              <div className="px-5 pb-4 pt-5">
                <div className="text-[9px] font-medium uppercase tracking-[0.24em] text-[#8A6D53]">
                  BRAND STUDIO
                </div>
                <h1
                  className="mt-1.5 max-w-[300px] text-[29px] leading-[0.98] text-[#2B211A]"
                  style={{ fontFamily: "'Cormorant Garamond', serif" }}
                >
                  Создавай бренд, который работает вместе с тобой
                </h1>
                <p className="mt-2 max-w-[310px] text-[12px] leading-[1.45] text-[#655141]">
                  Одно пространство для создания, развития и ежедневной работы с брендом.
                </p>
              </div>
              <picture className="block h-[240px] w-full overflow-hidden border-t border-[#E7DCCB] bg-[#F2EBDD]">
                <img
                  src="/brand-studio/brand-studio-hero-desktop-1536x561.jpeg"
                  alt=""
                  width="1536"
                  height="561"
                  className="block h-[240px] w-full object-cover object-[76%_center]"
                  fetchPriority="high"
                  loading="eager"
                  decoding="async"
                />
              </picture>
            </section>

            <section
              className="relative hidden aspect-[1536/561] w-full overflow-hidden rounded-[18px] border border-[#E2D6C5] bg-[#F2EBDD] md:block"
              data-testid="brand-studio-v2-hero"
            >
              <picture className="absolute inset-0 block h-full w-full">
                <img
                  src="/brand-studio/brand-studio-hero-desktop-1536x561.jpeg"
                  alt=""
                  width="1536"
                  height="561"
                  className="block h-full w-full object-contain"
                  fetchPriority="high"
                  loading="eager"
                  decoding="async"
                />
              </picture>
              <div className="absolute inset-y-0 left-0 z-20 flex w-[36%] items-center px-6">
                <div className="min-w-0">
                  <div className="text-[9px] font-medium uppercase tracking-[0.24em] text-[#8A6D53]">
                    BRAND STUDIO
                  </div>
                  <h1
                    className="mt-1.5 max-w-[250px] text-[27px] leading-[1.02] text-[#2B211A]"
                    style={{ fontFamily: "'Cormorant Garamond', serif" }}
                  >
                    Создавай бренд, который работает вместе с тобой
                  </h1>
                  <p className="mt-2 max-w-[245px] text-[11px] leading-[1.45] text-[#655141]">
                    Одно пространство для создания, развития и ежедневной работы с брендом.
                  </p>
                </div>
              </div>
            </section>

            {status === "loading" && (
              <section className="mt-4 grid min-h-[160px] place-items-center rounded-[26px] border border-[#E5D9C8] bg-[#FFFDF9]">
                <div className="inline-flex items-center gap-2 text-sm text-[#796652]">
                  <Loader2 size={16} className="animate-spin" /> Открываю Brand Studio…
                </div>
              </section>
            )}

            {status === "error" && (
              <section className="mt-4 rounded-[26px] border border-[#E2CFC8] bg-[#FFF8F4] px-5 py-6 md:px-7">
                <h2 className="font-serif text-2xl text-[#2B211A]">Не удалось открыть Brand Studio</h2>
                <p className="mt-2 text-sm leading-6 text-[#6D5842]">
                  Сохранённые данные на месте. Попробуй загрузить страницу ещё раз.
                </p>
                <button
                  type="button"
                  onClick={load}
                  className="mt-4 min-h-11 rounded-full border border-[#CDB899] bg-white px-5 text-sm text-[#4A3728]"
                >
                  Повторить
                </button>
              </section>
            )}

            {status === "ready" && !hasStory && !completed && (
              <section className="mt-4 rounded-[18px] border border-[#E1D4C2] bg-[#FFFDF9] px-4 py-5 sm:px-5 md:px-6">
                <div className="text-[9.5px] uppercase tracking-[0.22em] text-[#8A6D53]">НАЧАЛО</div>
                <h2 className="mt-1.5 font-serif text-[28px] leading-tight text-[#2B211A] sm:text-[30px] md:text-[34px]">
                  С чего начнём?
                </h2>
                <p className="mt-2 max-w-2xl text-[13px] leading-5 text-[#655141]">
                  Можно создать бренд с нуля или собрать в Brand Studio то, что у тебя уже есть.
                </p>

                <div className="mt-4 grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => nav("/brand-studio/create?start=new")}
                    className="min-h-[164px] rounded-[14px] border border-[#D9C9B5] bg-[#F3E9DB] p-3.5 text-left transition hover:-translate-y-0.5 sm:p-4"
                    data-testid="brand-studio-start-new"
                  >
                    <span className="grid h-9 w-9 place-items-center rounded-full bg-[#3D2A20] text-white">
                      <WandSparkles size={16} />
                    </span>
                    <span className="mt-4 block font-serif text-[21px] leading-[1.02] text-[#2B211A] sm:text-[24px]">Создать бренд</span>
                    <span className="mt-2 block text-[11.5px] leading-[1.45] text-[#6B5540] sm:text-[12.5px] sm:leading-5">
                      Начнём с рассказа о твоём деле.
                    </span>
                  </button>

                  <button
                    type="button"
                    onClick={() => nav("/brand-studio/create?start=existing")}
                    className="min-h-[164px] rounded-[14px] border border-[#D9C9B5] bg-white p-3.5 text-left transition hover:-translate-y-0.5 sm:p-4"
                    data-testid="brand-studio-start-existing"
                  >
                    <span className="grid h-9 w-9 place-items-center rounded-full bg-[#EFE4D4] text-[#755D46]">
                      <Layers3 size={16} />
                    </span>
                    <span className="mt-4 block font-serif text-[20px] leading-[1.02] text-[#2B211A] sm:text-[24px]">У меня уже есть бренд</span>
                    <span className="mt-2 block text-[11.5px] leading-[1.45] text-[#6B5540] sm:text-[12.5px] sm:leading-5">
                      Соберём существующие материалы в систему.
                    </span>
                  </button>
                </div>
              </section>
            )}

            {status === "ready" && (hasStory || completed) && (
              <section
                className="mt-4 rounded-[18px] border border-[#E1D4C2] bg-[#FFFDF9] px-4 py-4 sm:px-5 md:px-6"
                data-testid="brand-studio-active-brand"
              >
                <div className="inline-flex items-center gap-2 rounded-full bg-[#EEF0DF] px-3 py-1.5 text-[11.5px] text-[#586044]">
                  <Check size={13} /> {completed ? "Визуальная система утверждена" : `Рассказ подтверждён · V${story.source_version}`}
                </div>

                <h2 className="mt-3 font-serif text-[27px] leading-[1.02] text-[#2B211A] sm:text-[29px] md:text-[33px]">
                  {completed ? "Твой бренд готов" : "Продолжим собирать твой бренд"}
                </h2>
                <p className="mt-2 max-w-2xl text-[13px] leading-5 text-[#655141]">
                  {completed ? "Твои решения сохранены. Вернись к ним в своём пространстве." : "Исходный рассказ сохранён. Дальше проверим, как Brand Studio поняла твой бренд."}
                </p>

                <div className="mt-4 flex flex-wrap gap-3">
                  <button
                    type="button"
                    onClick={() => nav(completed ? "/brand-studio/my-brand" : workspace?.next || "/brand-studio/create")}
                    className="inline-flex min-h-11 items-center gap-2 rounded-full border border-[#D9C9B5] bg-[#F3E9DB] px-5 text-[13px] font-medium text-[#3D2A20] transition-colors hover:bg-[#EADCC8] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#7D6248]"
                    data-testid="brand-studio-continue"
                  >
                    {completed ? "Открыть Мой бренд" : "Продолжить создание"} <ArrowRight size={15} />
                  </button>
                  <button
                    type="button"
                    onClick={() => nav("/brand-studio/create?intent=edit")}
                    className="inline-flex min-h-11 items-center gap-2 rounded-full border border-[#D2C1AA] bg-white px-4 text-[13px] font-medium text-[#4A3728] transition-colors hover:bg-[#F8F1E8] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#7D6248]"
                    data-testid="brand-studio-edit-brand"
                  >
                    <PenLine size={14} /> Редактировать бренд
                  </button>
                  <button
                    type="button"
                    onClick={() => nav("/brand-studio/create?view=story")}
                    className="inline-flex min-h-11 items-center gap-2 rounded-full border border-[#D2C1AA] bg-white px-4 text-[13px] text-[#4A3728]"
                    data-testid="brand-studio-view-story"
                  >
                    <FileText size={14} /> Посмотреть рассказ
                  </button>
                </div>

                <div className="mt-3 border-t border-[#EEE3D5] pt-3 text-[11px] leading-5 text-[#8A745F]">
                  {completed ? "Цвета и шрифты утверждены · Логотип необязателен" : "Рассказ ✓ · Понимание · Визуальный поиск · Фирменная система"}
                </div>
              </section>
            )}
          </div>

          <PossibilityRail nav={nav} />
        </div>

      </div>
    </BrandStudioShellV2>
  );
}
